<template>
    <div class="under-header p-10 blur relative" id="tabbed-section" :style="backgroundImageStyle">
        <div class="spinning-background-2"></div>
        <div class="mobile-background">
            <h1 class="font-medium layoutHeader blur">{{ page?.altTitle || page?.title || 'Untitled Page' }}</h1>
            <Splitter class="featuredSplitter">
                <SplitterPanel class="isotopesPanel" :size="35" :size-small="100">
                    <div class="pt-4 pr-8 relative">
                        <div v-if="page?.content" class="page-content large">
                            <div v-html="processedContent(page.content)"></div>
                            <a href="/pages/9" class="cta cta--isotopes cursor-pointer text-white px-4 py-2 rounded-lg items-center desktop-only" @click.prevent="goToPage(9, $refs.slideContent)" title="Navigate to enquiry page">Click here to enquire</a>
                        </div>
                        <div v-if="page?.wistiaEmbed && isDirectVideoUrl(page.wistiaEmbed)" class="wistia-embed">
                            <OfflineVideo :src="resolveVideoSrc(page.wistiaEmbed)" />
                        </div>
                        <div v-else-if="page?.wistiaEmbed" class="wistia-embed">
                            <iframe :src="getWistiaEmbedUrl(page.wistiaEmbed)" allowfullscreen="false" webkit-playsinline="true" playsinline frameborder="0"></iframe>
                        </div>
                        <div v-if="page?.contentImage" class="wistia-embed">
                            <img :src="resolveImageUrl(page.contentImage)" alt="Content Image">
                        </div>
                        <div v-if="displayTabImageSrc" class="wistia-embed rounded">
                            <img :key="displayTabImageSrc" :src="displayTabImageSrc" :alt="`${page?.tabbedContent?.[activeTabIndex]?.title || 'Tab'} image`">
                        </div>
                    </div>
                </SplitterPanel>
                <SplitterPanel class="isotopesPanel" :size="65" :size-small="100">
                    <div class="pt-4 pl-4">
                        <div v-if="page?.tabbedContent && page.tabbedContent.length > 0">
                            <Tabs v-model:value="activeTabIndex">
                                <TabList>
                                    <Tab v-for="(tab, idx) in page.tabbedContent" :key="tab.title" :value="idx">{{ tab.title }}</Tab>
                                </TabList>
                                <TabPanels class="pt-2 p-5 blur">
                                    <TabPanel v-for="(tab, idx) in page.tabbedContent" :key="tab.title" :value="idx">
                                        <div v-html="processedContent(tab.content)"></div>
                                    </TabPanel>
                                </TabPanels>
                            </Tabs>
                        </div>
                        <a href="/pages/9" class="cta cursor-pointer text-white mobile-only" @click.prevent="goToPage(9, $refs.slideContent)" title="Navigate to enquiry page">Click here to enquire</a>
                    </div>
                </SplitterPanel>
            </Splitter>
        </div>
    </div>
</template>
<script setup>
import Splitter from 'primevue/splitter';
import SplitterPanel from 'primevue/splitterpanel';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import OfflineVideo from '../components/OfflineVideo.vue';
import { useNavigation } from '../composables/useNavigation.js';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { processContent } from '../utils/markdown.js';
import { useCommonStyles } from '../composables/useCommonStyles.js';
import { useMediaHelpers } from '../composables/useMediaHelpers.js';
import { isConnected } from '../composables/useConnectivityStatus.js';
import ApiService from '../services/ApiService.js';

const props = defineProps({
  page: Object,
  settings: Object
});

// Reactive tab index for image display
const activeTabIndex = ref(0);

const { isDirectVideoUrl, resolveVideoSrc, resolveImageUrl, getWistiaEmbedUrl } = useMediaHelpers();

// Use common styles composable
const { backgroundImageStyle } = useCommonStyles(props);

// Reactive data for component media
const tabbedContentWithMedia = ref([]);
const isLoadingComponentMedia = ref(false);
const displayTabImageSrc = ref(null);
let tabImageBlobUrl = null;

const revokeTabImageBlobUrl = () => {
  if (!tabImageBlobUrl) {
    return;
  }

  URL.revokeObjectURL(tabImageBlobUrl);
  tabImageBlobUrl = null;
};

const resolveDisplayTabImageSrc = async (url) => {
  console.log('[TabbedLayout] resolveDisplayTabImageSrc called with:', url);
  revokeTabImageBlobUrl();

  if (!url) {
    console.log('[TabbedLayout] No URL provided, clearing image');
    displayTabImageSrc.value = null;
    return;
  }

  if (isConnected.value || !('caches' in window)) {
    console.log('[TabbedLayout] Online or no cache support, using original URL:', url);
    displayTabImageSrc.value = url;
    return;
  }

  try {
    const cache = await caches.open('app-cache-v1-images');
    const cachedResponse = await cache.match(url);

    if (cachedResponse) {
      console.info('[TabbedLayout] Cache hit for tab image:', url);
      const blob = await cachedResponse.blob();
      tabImageBlobUrl = URL.createObjectURL(blob);
      console.log('[TabbedLayout] Created blob URL:', tabImageBlobUrl);
      displayTabImageSrc.value = tabImageBlobUrl;
      console.log('[TabbedLayout] Set displayTabImageSrc to:', displayTabImageSrc.value);
      return;
    }

    console.warn('[TabbedLayout] Cache miss for tab image:', url, '— using original URL (may fail offline)');
  } catch (error) {
    console.warn('[TabbedLayout] Unable to resolve cached tab image:', url, error);
  }

  console.log('[TabbedLayout] Fallback: setting displayTabImageSrc to original URL:', url);
  displayTabImageSrc.value = url;
};

const activeTabImage = computed(() => {
  const apiImage = resolveImageUrl(tabbedContentWithMedia.value?.[activeTabIndex.value]?.image);
  if (apiImage) {
    return apiImage;
  }

  return resolveImageUrl(props.page?.tabbedContent?.[activeTabIndex.value]?.image);
});

watch(activeTabImage, (url, oldUrl) => {
  console.log('[TabbedLayout] Tab image changed:', { oldUrl, newUrl: url, activeTabIndex: activeTabIndex.value });
  void resolveDisplayTabImageSrc(url);
}, { immediate: true });

const handleConnectionChange = () => {
  void resolveDisplayTabImageSrc(activeTabImage.value);
};

onMounted(() => {
  window.addEventListener('app:connectivity-change', handleConnectionChange);
});

onUnmounted(() => {
  window.removeEventListener('app:connectivity-change', handleConnectionChange);
  revokeTabImageBlobUrl();
});

// Function to fetch component media using cached API service
const fetchComponentMedia = async () => {
  if (!props.page?.tabbedContent || props.page.tabbedContent.length === 0) {
    return;
  }

  isLoadingComponentMedia.value = true;
  
  try {
    const response = await ApiService.getPageComponents(props.page.slug, 'tabbedContent');
    if (response && response.data) {
      tabbedContentWithMedia.value = response.data;
    }
  } catch (error) {
    console.error('Error fetching component media:', error);
  } finally {
    isLoadingComponentMedia.value = false;
  }
};

// Process second content area with markdown
const processedContent = (content) => {
  return content ? processContent(content) : '';
};

const { goToPage } = useNavigation();

// Watch for page changes and fetch component media
watch(() => props.page, (newPage) => {
  activeTabIndex.value = 0;
  if (newPage?.tabbedContent && newPage.tabbedContent.length > 0) {
    fetchComponentMedia();
  }
}, { immediate: true });
</script>
